import { useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../../../store/hooks/hooks";

import PaymentCheckoutTest from "../../CheckoutPage/PaymentCheckoutTest";
import { Box, Flex, Heading, Text, useToast, VStack } from "@chakra-ui/react";
import {
  bookSeat,
  getTripById,
} from "../../../store/travelSlice/hikeTripsSlice";
import { useAuth } from "../../../auth/AuthContext";
import { TripDetailsPanel } from "./TripDetailsPanel";
import MapDisplay from "../../../LocationsModule/MapDisplay";

import { useEffect, useMemo, useRef, useState } from "react";
import { fetchUsersFromFirestore } from "../../../utils/firebaseHelpers";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";
import {
  CreateOrderPayload,
  useCreateOrderMutation,
} from "../../../orderApi/orderApi";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../configs/firebase";
import AppPhoneInput, { AppPhoneCountryCode } from "../../Shared/PhoneInput";

const cardsBorderRadii ="8px";

type DriverUser = {
  id: string;
  name: string;
  email: string;
  photoURL: string;
  role: string;
  createdAt: string;
  phone?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  businessName?: string;
};

type UserProfileData = {
  phone?: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  name?: string;
  displayName?: string;
  email?: string;
};

const DEFAULT_SA_COUNTRY_CODE: AppPhoneCountryCode = "ZA";
const DEFAULT_SA_LOCAL_NUMBER = "647367388";
const DEFAULT_SA_INTERNATIONAL_NUMBER = "27647367388";

const normalizeDigits = (value: any) => String(value || "").replace(/\D/g, "");

const normalizeSouthAfricaLocalNumber = (value: any) => {
  const digits = normalizeDigits(value);

  if (!digits) {
    return DEFAULT_SA_LOCAL_NUMBER;
  }

  if (digits.startsWith("27")) {
    return digits.slice(2).replace(/^0+/, "") || DEFAULT_SA_LOCAL_NUMBER;
  }

  if (digits.startsWith("265")) {
    return digits.slice(3).replace(/^0+/, "") || DEFAULT_SA_LOCAL_NUMBER;
  }

  if (digits.startsWith("0")) {
    return digits.slice(1) || DEFAULT_SA_LOCAL_NUMBER;
  }

  return digits || DEFAULT_SA_LOCAL_NUMBER;
};

const normalizeCheckoutWhatsAppPhone = (value: any) => {
  const localNumber = normalizeSouthAfricaLocalNumber(value);

  if (!localNumber) {
    return DEFAULT_SA_INTERNATIONAL_NUMBER;
  }

  if (localNumber.startsWith("27")) {
    return localNumber;
  }

  return `27${localNumber.replace(/^0+/, "")}`;
};

const applyPhoneToInputState = (
  rawPhone: string,
  setCountry: (value: AppPhoneCountryCode) => void,
  setLocal: (value: string) => void,
) => {
  setCountry(DEFAULT_SA_COUNTRY_CODE);
  setLocal(normalizeSouthAfricaLocalNumber(rawPhone));
};

const getCustomerName = (user: any, profile: UserProfileData | null) => {
  return String(
    profile?.displayName ||
      profile?.name ||
      user?.displayName ||
      localStorage.getItem("displayName") ||
      "Customer",
  ).trim();
};

const getOwnerName = (trip: any, driver: DriverUser) => {
  return String(
    driver.businessName ||
      driver.name ||
      trip?.driverName ||
      trip?.ownerName ||
      "Trip Owner",
  ).trim();
};

const getPublicWebUrl = () => {
  return String(
    process.env.REACT_APP_PUBLIC_WEB_URL ||
      process.env.REACT_APP_WEB_URL ||
      "https://pickdengu.mw",
  ).replace(/\/+$/, "");
};

const getHikeBrowseUrl = () => `${getPublicWebUrl()}/hike`;

const getHikeCheckoutUrl = (tripId?: string) => {
  const cleanTripId = String(tripId || "").trim();

  if (!cleanTripId) return getHikeBrowseUrl();

  return `${getPublicWebUrl()}/hike/checkout/${cleanTripId}`;
};

const getHikeNotificationPolicy = () => ({
  sendCustomerCreated: true,
  sendCustomerInvoiceOnCreated: true,

  // Seller must not receive unpaid invoice/status.
  // Seller receives only paid invoice/payment confirmation.
  sendSellerCreatedStatus: false,

  suppressSellerConfirmReject: true,
  suppressSellerAvailabilityConfirmation: true,
  sellerNotificationMode: "status_view_only",

  sendSellerPaidStatus: true,
  sendCustomerInvoiceOnPaid: true,
  sendSellerInvoiceOnPaid: true,

  sellerPaidTemplateKey: "order_payment_invoice",
  customerPaidTemplateKey: "order_payment_confirmed",
});

const TripCheckoutPage = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const trip = useAppSelector((state) => getTripById(state, tripId || ""));
  const { user } = useAuth();
  const { theme } = useCustomTheme();

  const [createOrderMutation] = useCreateOrderMutation();

  const [directions, setDirections] =
    useState<google.maps.DirectionsResult | null>(null);
  const [distance, setDistance] = useState("");
  const [duration, setDuration] = useState("");
  const [cost, setCost] = useState<number | null>(null);
  const [center, setCenter] = useState({ lat: -13.9626, lng: 33.7741 });

  const [driver, setDriver] = useState<DriverUser>({
    id: "",
    name: "",
    email: "",
    photoURL: "",
    role: "",
    createdAt: "",
    phone: "",
    phoneNumber: "",
    whatsappNumber: "",
    businessName: "",
  });

  const cars = useAppSelector((state) => state.hikeCars.list);
  const [tripCar, setTripCar] = useState<any | null>(null);
  const [customerProfile, setCustomerProfile] =
    useState<UserProfileData | null>(null);

  const [customerPhoneCountryCode, setCustomerPhoneCountryCode] =
    useState<AppPhoneCountryCode>(DEFAULT_SA_COUNTRY_CODE);
  const [customerPhoneLocalNumber, setCustomerPhoneLocalNumber] = useState(
    DEFAULT_SA_LOCAL_NUMBER,
  );
  const [profilePhoneLoaded, setProfilePhoneLoaded] = useState(false);

  const bookingHandledRef = useRef(false);
  const createdOrderIdRef = useRef<string | null>(null);

  const pricePerKm = {
    DRIVING: 5,
    BICYCLING: 2,
    TRANSIT: 10,
  };

  const customerPhone = useMemo(() => {
    return normalizeCheckoutWhatsAppPhone(
      customerPhoneLocalNumber || DEFAULT_SA_LOCAL_NUMBER,
    );
  }, [customerPhoneLocalNumber]);

  const shouldShowPhoneInput = useMemo(() => {
    return !user?.uid || !customerPhone;
  }, [user?.uid, customerPhone]);

  useEffect(() => {
    const loadCustomerProfile = async () => {
      const localPhone = normalizeDigits(
        localStorage.getItem("whatsappNumber") ||
          localStorage.getItem("phoneNumber") ||
          localStorage.getItem("userPhoneNumber") ||
          DEFAULT_SA_INTERNATIONAL_NUMBER,
      );

      if (!user?.uid) {
        applyPhoneToInputState(
          localPhone,
          setCustomerPhoneCountryCode,
          setCustomerPhoneLocalNumber,
        );

        setProfilePhoneLoaded(true);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));

        if (snap.exists()) {
          const data = snap.data() as UserProfileData;
          setCustomerProfile(data);

          const profilePhone = normalizeDigits(
            data.whatsappNumber ||
              data.phoneNumber ||
              data.phone ||
              localPhone ||
              DEFAULT_SA_INTERNATIONAL_NUMBER,
          );

          applyPhoneToInputState(
            profilePhone,
            setCustomerPhoneCountryCode,
            setCustomerPhoneLocalNumber,
          );
        } else {
          applyPhoneToInputState(
            localPhone || DEFAULT_SA_INTERNATIONAL_NUMBER,
            setCustomerPhoneCountryCode,
            setCustomerPhoneLocalNumber,
          );
        }
      } catch (error) {
        console.error("Failed to load user profile phone:", error);

        applyPhoneToInputState(
          localPhone || DEFAULT_SA_INTERNATIONAL_NUMBER,
          setCustomerPhoneCountryCode,
          setCustomerPhoneLocalNumber,
        );
      } finally {
        setProfilePhoneLoaded(true);
      }
    };

    loadCustomerProfile();
  }, [user?.uid]);

  useEffect(() => {
    if (!trip?.from?.address || !trip?.to?.address || !window.google) return;

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: trip.from.address,
        destination: trip.to.address,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          setDirections(result);
          const leg = result.routes[0].legs[0];
          setDistance(leg.distance?.text || "");
          setDuration(leg.duration?.text || "");
          const km = parseFloat(
            leg.distance?.text.replace(/[^0-9.]/g, "") || "0",
          );
          setCost(Number((km * pricePerKm.DRIVING).toFixed(2)));
          setCenter({
            lat: leg.start_location.lat(),
            lng: leg.start_location.lng(),
          });
        } else {
          console.error("Directions fetch failed:", status);
          toast({
            title: "Failed to load route",
            description: "Map directions could not be loaded for this trip.",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
        }
      },
    );

    const loadUsers = async () => {
      try {
        const users = await fetchUsersFromFirestore();
        const found = (users as any[]).find(
          (u: any) => u.id === trip?.driverId,
        );

        if (found) {
          setDriver({
            id: found.id || "",
            name: found.name || found.displayName || "",
            email: found.email || "",
            photoURL: found.photoURL || "",
            role: found.role || "",
            createdAt: found.createdAt || "",
            phone: found.phone || "",
            phoneNumber: found.phoneNumber || "",
            whatsappNumber: found.whatsappNumber || "",
            businessName: found.businessName || "",
          });
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast({
          title: "Driver info unavailable",
          description: "Could not load the driver details right now.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
      }
    };

    loadUsers();

    const activeCar = cars.find((u: any) => u._id === trip?.carId);
    setTripCar(activeCar || null);
  }, [trip, cars, toast]);

  useEffect(() => {
    if (!trip && tripId) {
      // dispatch(getTripById(tripId));
    }
  }, [trip, tripId, dispatch]);

  const ownerUserId = useMemo(() => {
    const tripAny = trip as any;

    return String(
      tripAny?.ownerUserId ||
        tripAny?.userId ||
        trip?.driverId ||
        driver.id ||
        "",
    ).trim();
  }, [trip, driver.id]);

  const ownerName = useMemo(() => getOwnerName(trip, driver), [trip, driver]);

  const ownerPhoneNumber = useMemo(() => {
    return normalizeCheckoutWhatsAppPhone(
      driver.whatsappNumber ||
        driver.phoneNumber ||
        driver.phone ||
        DEFAULT_SA_INTERNATIONAL_NUMBER,
    );
  }, [driver]);

  const customerName = useMemo(
    () => getCustomerName(user, customerProfile),
    [user, customerProfile],
  );

  const customerEmail = useMemo(() => {
    const profileEmail = String(customerProfile?.email || "").trim();
    const userEmail = String(user?.email || "").trim();

    if (userEmail) return userEmail;
    if (profileEmail) return profileEmail;

    if (customerPhone) {
      return `guest-${customerPhone}@pickdengu.local`;
    }

    return "";
  }, [customerProfile?.email, user?.email, customerPhone]);

  const pricingBreakdown = useMemo(() => {
    const productSubtotal = Number(trip?.priceMk || 0);
    const serviceFee =
      productSubtotal > 0
        ? Number(Math.max(productSubtotal * 0.05, 500).toFixed(2))
        : 0;
    const gatewayFee = Number((productSubtotal * 0.025).toFixed(2));
    const checkoutTotal = Number(
      Math.max(0, productSubtotal + serviceFee).toFixed(2),
    );
    const sellerCommissionTotal = serviceFee;
    const sellerNetPayableTotal = Number(
      Math.max(0, productSubtotal - serviceFee - gatewayFee).toFixed(2),
    );

    return {
      currency: "MWK",
      productSubtotal,
      serviceFee,
      deliveryFee: 0,
      discountAmount: 0,
      gatewayFee,
      total: checkoutTotal,
      sellerCommissionTotal,
      sellerNetPayableTotal,
      driverEarningTotal: 0,
      deliveryMargin: 0,
      platformNetRevenue: serviceFee,
      deliveryDistanceKm: 0,
      totalActualWeightKg: 0,
      totalVolumetricWeightKg: 0,
      billableWeightKg: 0,
      bulkySurcharge: 0,
    };
  }, [trip?.priceMk]);

  // Keep the payment amount identical to the amount persisted on the order.
  // This mirrors the working product/accommodation/car-hire checkout pattern.
  const payableAmount = Number(pricingBreakdown.total || 0);

  const createHikeOrder = async (): Promise<string | null> => {
    try {
      if (!tripId || !trip) {
        toast({
          title: "Trip not found",
          description: "Unable to create booking order for this trip.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (!customerPhone) {
        toast({
          title: "Missing WhatsApp number",
          description:
            "Please add a WhatsApp number to receive your trip booking confirmation and invoice.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return null;
      }

      if (!customerEmail) {
        toast({
          title: "Missing email",
          description: "An email is required before payment can continue.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return null;
      }

      if (!ownerUserId) {
        toast({
          title: "Trip owner is missing",
          description:
            "This trip is missing a valid owner/driver ID. Please edit and save the trip again from the owner account before accepting bookings.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return null;
      }

      const checkoutLink = getHikeCheckoutUrl(tripId);
      const notificationPolicy = getHikeNotificationPolicy();

      const orderPayload = {
        userId: user?.uid ?? "guest",
        email: customerEmail,
        phoneNumber: customerPhone,
        name: customerName,
        category: "hike",
        serviceCategory: "hike",
        orderType: "hike",
        source: "website",
        channel: "website",
        checkoutMode: "pickup",

        ownerUserId,
        sellerUserId: ownerUserId,
        ownerName,
        sellerName: ownerName,
        ownerPhoneNumber,
        sellerPhoneNumber: ownerPhoneNumber,

        notificationPolicy,

        metadata: {
          source: "website",
          channel: "website",
          category: "hike",
          serviceCategory: "hike",
          orderType: "hike",
          tripId,
          checkoutLink,
          paymentReminderLink: checkoutLink,
          customerName,
          customerPhone,
          ownerUserId,
          sellerUserId: ownerUserId,
          ownerName,
          sellerName: ownerName,
          ownerPhoneNumber,
          sellerPhoneNumber: ownerPhoneNumber,
          notificationPolicy,
        },

        items: [
          {
            title: `Trip Booking: ${trip.from?.address || "From"} → ${trip.to?.address || "To"}`,
            description: `Seat booking for ${trip.date || ""} | Depart: ${trip.departTime || ""} | Arrive: ${trip.arriveTime || ""}`,
            category: "hike",
            imageUrl: "",
            price: Number(trip.priceMk || 0),
            quantity: 1,
            sellerUserId: ownerUserId,
            ownerId: ownerUserId,
            userId: ownerUserId,
            sellerBusinessName: ownerName,
            sellerName: ownerName,
            ownerName,
            sellerPhoneNumber: ownerPhoneNumber,
            ownerPhoneNumber,
            dispatchWarehouseId: "",
            selectedVariants: [],
            variantSummary: "",
            isPhysical: false,
            weightKg: 0,
            lengthCm: 0,
            widthCm: 0,
            heightCm: 0,
            bulky: false,
            meta: {
              tripId,
              checkoutLink,
              paymentReminderLink: checkoutLink,
              driverId: trip.driverId || driver.id || "",
              ownerUserId,
              sellerUserId: ownerUserId,
              ownerName,
              sellerName: ownerName,
              ownerPhoneNumber,
              sellerPhoneNumber: ownerPhoneNumber,
              customerName,
              customerPhone,
              from: trip.from || null,
              to: trip.to || null,
              date: trip.date || "",
              departTime: trip.departTime || "",
              arriveTime: trip.arriveTime || "",
            },
          } as any,
        ],
        total: payableAmount,
        currency: "MWK",
        fromAddress: trip.from?.address || "",
        toAddress: trip.to?.address || "",
        fromLocation:
          typeof trip.from?.lat === "number" &&
          typeof trip.from?.lng === "number"
            ? { lat: trip.from.lat, lng: trip.from.lng }
            : undefined,
        toLocation:
          typeof trip.to?.lat === "number" && typeof trip.to?.lng === "number"
            ? { lat: trip.to.lat, lng: trip.to.lng }
            : undefined,
        deliveryInstructions: `Trip ID: ${tripId}`,
        pricingBreakdown,
        payment: {
          method: "",
          provider: "",
          status: "pending",
          cardNumber: "",
          cardHolder: "",
          expiryDate: "",
          cvv: "",
        } as any,
        status: "pending",
      } as CreateOrderPayload & Record<string, any>;

      console.log("🚌 Creating hike order payload:", orderPayload);

      const response = await createOrderMutation(orderPayload).unwrap();

      console.log("🟢 Full hike order response:", response);

      const createdId = response?._id || null;

      if (!createdId) {
        toast({
          title: "Order created but ID missing",
          description: "The server response did not include an order ID.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return null;
      }

      createdOrderIdRef.current = createdId;

      // Same pattern as Accommodation/Car Hire:
      // create the service booking against the pending order and let the
      // existing payment backend/webhook own the paid transition.
      if (!bookingHandledRef.current) {
        const booking = await dispatch(
          bookSeat({
            tripId,
            orderId: createdId,
            userId: user?.uid || "guest",
            seatsBooked: 1,
            paidVia: "",
            paymentStatus: "pending",
          }),
        ).unwrap();

        bookingHandledRef.current = true;
        console.log("✅ Pending trip booking created:", booking);
      }

      toast({
        title: "Trip booking order created",
        description: "Proceeding to payment flow.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      return createdId;
    } catch (err: any) {
      console.error("❌ createHikeOrder error:", err);
      console.error("❌ createHikeOrder error response:", err?.response?.data);

      toast({
        status: "error",
        title: "Failed to create trip booking order",
        description:
          err?.data?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Please try again.",
        duration: 5000,
        isClosable: true,
      });

      return null;
    }
  };

  if (!trip) {
    return <Text color={theme.text}>Loading trip details...</Text>;
  }

  return (
    <Box
      w="100%"
      bg={theme.surfaceAlt}
      py={{ base: 3, md: 4 }}
      px={{ base: 3, md: 4 }}
      mx="auto"
      minH="100vh"
    >
      <Box
        border="1px solid"
        borderColor={theme.border}
        bg={theme.surface}
        borderRadius={cardsBorderRadii}
        p={{ base: 4, md: 5 }}
        boxShadow={theme.shadowSm}
      >
        <Heading color={theme.text} size="lg" mb={4}>
          Trip Checkout
        </Heading>

        <Flex gap={6} flexDirection={{ base: "column", md: "row" }}>
          <Box w="100%">
            <TripDetailsPanel
              from={trip.from}
              to={trip.to}
              date={trip.date}
              driver={driver}
              car={tripCar}
              seatsAvailable={trip.seatsAvailable}
              priceMk={trip.priceMk}
              departTime={trip.departTime}
              arriveTime={trip.arriveTime}
            />

            {profilePhoneLoaded && shouldShowPhoneInput && (
              <Box
                borderWidth="1px"
                borderColor={theme.border}
                bg={theme.surface}
                borderRadius={cardsBorderRadii}
                mt={4}
                p={4}
                boxShadow={theme.shadowSm}
              >
                <AppPhoneInput
                  label="WhatsApp number for booking updates"
                  value={customerPhoneLocalNumber}
                  countryCode={customerPhoneCountryCode}
                  placeholder="064 736 7388"
                  onCountryChange={(value) => {
                    setCustomerPhoneCountryCode(value);
                    if (value === "ZA" && !customerPhoneLocalNumber) {
                      setCustomerPhoneLocalNumber(DEFAULT_SA_LOCAL_NUMBER);
                    }
                  }}
                  onNumberChange={setCustomerPhoneLocalNumber}
                  theme={theme}
                />

                <Text mt={2} fontSize="xs" color={theme.textSubtle}>
                  We will send your booking confirmation, payment update, and
                  invoice to this WhatsApp number.
                </Text>
              </Box>
            )}

            {profilePhoneLoaded && user?.uid && customerPhone && (
              <Box
                borderWidth="1px"
                borderColor={theme.border}
                bg={theme.surface}
                borderRadius={cardsBorderRadii}
                mt={4}
                p={4}
                boxShadow={theme.shadowSm}
              >
                <Text fontSize="sm" fontWeight="700" color={theme.text}>
                  WhatsApp updates
                </Text>
                <Text mt={1} fontSize="sm" color={theme.textSubtle}>
                  Booking notifications will be sent to your profile number: +
                  {customerPhone}
                </Text>
              </Box>
            )}

            <Box
              borderWidth="1px"
              borderColor={theme.border}
              bg={theme.surface}
              borderRadius={cardsBorderRadii}
              mt={4}
              boxShadow={theme.shadowSm}
              overflow="hidden"
            >
              <PaymentCheckoutTest
                amount={payableAmount}
                onCreateOrder={createHikeOrder}
                shouldClearCart={false}
                pendingPath={(id: string) => `/checkout-pending/${id}`}
                successPath={(id: string) => `/checkout-success/${id}`}
              />
            </Box>
          </Box>

          <Box
            borderWidth="1px"
            borderColor={theme.border}
            bg={theme.surface}
            borderRadius={cardsBorderRadii}
            w="100%"
            px={4}
            py={4}
            maxW={{ base: "100%", md: "320px" }}
            boxShadow={theme.shadowSm}
          >
            {directions && (
              <Box w="100%">
                <VStack
                  w="100%"
                  align="start"
                  mb={4}
                  spacing={2}
                  p={3}
                  borderRadius={cardsBorderRadii}
                  border="1px solid"
                  borderColor={theme.border}
                  bg={theme.surfaceAlt}
                >
                  <Text color={theme.text}>
                    <strong>Distance:</strong> {distance}
                  </Text>
                  <Text color={theme.text}>
                    <strong>Duration:</strong> {duration}
                  </Text>
                  {cost !== null && (
                    <Text color={theme.text}>
                      <strong>Total Payable:</strong> MWK {payableAmount}
                    </Text>
                  )}
                </VStack>

                <MapDisplay
                  height="440px"
                  directions={directions}
                  center={center}
                />
              </Box>
            )}

            {!directions && (
              <Text color={theme.textSubtle}>Loading route map...</Text>
            )}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default TripCheckoutPage;
